import React from 'react';
import HomeNav from '../navigations/homenav';
import Footer from '../navigations/Footer';
import { UsersIcon, HeartIcon, EyeIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid, ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import { useLanguageContext } from '../navigations/homenav';
import { Helmet } from 'react-helmet-async';

// Team Member Card Component
const TeamMemberCard = ({ name, title, imageUrl, bio }) => (
  <div className="text-center bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
    <img
      className="w-32 h-32 mx-auto rounded-full object-cover"
      src={imageUrl}
      alt={name}
    />
    <h3 className="mt-4 text-xl font-semibold text-gray-800">{name}</h3>
    <p className="text-sky-600">{title}</p>
    <p className="mt-2 text-gray-600 text-sm">{bio}</p>
  </div>
);

// Value Card Component
const ValueCard = ({ icon, title, children }) => (
  <div className="bg-sky-50 p-6 rounded-lg">
    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-sky-100 text-sky-600">
      {icon}
    </div>
    <h3 className="mt-5 text-lg font-semibold text-gray-800">{title}</h3>
    <p className="mt-2 text-base text-gray-600">{children}</p>
  </div>
);

// Testimonial Card Component
const TestimonialCard = ({ quote, name, title, avatar, rating }) => {
  const stars = [];
  for (let i = 0; i < 5; i++) {
    stars.push(i < rating ? <StarIconSolid key={i} className="h-5 w-5 text-yellow-400" /> : <StarIconOutline key={i} className="h-5 w-5 text-yellow-400" />);
  }

  return (
    <div className="bg-white p-8 rounded-lg shadow-lg relative text-center">
      <p className="text-gray-600 italic">"{quote}"</p>
      <div className="mt-6 flex flex-col items-center">
        <img src={avatar} alt={name} className="w-16 h-16 rounded-full object-cover"/>
        <div className="mt-4">
          <p className="font-semibold text-gray-800">{name}</p>
          <p className="text-sm text-gray-500">{title}</p>
          <div className="flex justify-center mt-2">{stars}</div>
        </div>
      </div>
      <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-white p-3 rounded-full shadow-md">
        <ChatBubbleBottomCenterTextIcon className="h-6 w-6 text-sky-600 transform rotate-180" />
      </div>
    </div>
  );
};

const AboutUsPage = () => {
  const { t } = useLanguageContext();
  
  return (
    <div className="bg-gray-50 text-gray-800">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>About AAAPD – Who We Are</title>
        <meta
          name="description"
          content="Learn about the Addis Ababa Association of Persons with Disabilities (AAAPD), our mission, vision, values, and commitment to empowering persons with disabilities."
        />
        <link rel="canonical" href="https://aaapd.org.et/whoweare" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <HomeNav />

      <main>
        {/* Hero Section */}
        <section
          className="py-24 bg-center text-white relative"
          style={{
            backgroundImage: "url('/aaapdmainlogo.jpg')",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            minHeight: "400px"
          }}
        >
          <div className="absolute inset-0 bg-slate-800 bg-opacity-60"></div>
          <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight">{t('about')}</h1>
            <p className="mt-4 text-lg md:text-xl text-sky-100 max-w-3xl mx-auto">
              {t('aboutDescription')}
            </p>
          </div>
        </section>

        {/* 'Together We Make a Difference' Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
            {/* Left side: Text content */}
            <div>
              <p className="text-sm font-semibold text-yellow-500 uppercase tracking-wider">{t('about')}</p>
              <h2 className="mt-2 text-4xl md:text-5xl font-bold text-gray-900">
                {t('aboutTitle')}
              </h2>
              <p className="mt-6 text-lg text-gray-600">
                {t('aboutDescription')}
              </p>
              <div className="mt-8">
                <a href="/contactus" className="inline-block px-8 py-3 rounded-md text-lg font-medium bg-yellow-400 text-gray-800 hover:bg-yellow-500 transition-colors duration-300">
                  {t('contactUs')}
                </a>
              </div>
            </div>
            {/* Right side: Image collage */}
            <div className="relative h-96">
              <img src="/galleryimagemain1.jpg" alt="Child smiling" className="absolute top-0 right-0 w-3/4 rounded-lg shadow-lg"/>
              <img src="/galleryimagemain8.png" alt="Child in green beanie" className="absolute top-1/4 left-0 w-3/5 rounded-lg shadow-2xl border-4 border-white z-20"/>
              <img src="/galleryimagemain4.jpg" alt="Children playing" className="absolute bottom-0 left-1/3 w-2/3 rounded-lg shadow-xl border-4 border-white z-10"/>
            </div>
          </div>
        </section>

        {/* Our Story - Timeline Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">{t('aboutBackgroundTitle')}</h2>
              <div className="w-24 h-1 bg-gradient-to-r from-teal-500 to-cyan-500 mx-auto mb-6"></div>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {t('aboutBackgroundDesc')}
              </p>
            </div>

            <div className="relative">
  {/* Timeline line */}
  <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-teal-400 via-cyan-400 to-teal-400"></div>

  {/* Timeline items */}
  <div className="space-y-16">
    {/* 01 - Umbrella Association */}
    <div className="relative flex items-start">
      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg">
        <span className="text-white font-bold text-lg">01</span>
      </div>
      <div className="ml-8 flex-1">
        <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-teal-500">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('umbrellaTitle')}</h3>
          <p className="text-gray-700 leading-relaxed text-lg">{t('umbrellaText')}</p>
        </div>
      </div>
    </div>

    {/* 02 - Vision & Mission */}
    <div className="relative flex items-start">
      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full flex items-center justify-center shadow-lg">
        <span className="text-white font-bold text-lg">02</span>
      </div>
      <div className="ml-8 flex-1">
        <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-cyan-500">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('visionMissionTitle')}</h3>
          <p className="text-gray-700 leading-relaxed text-lg">{t('visionMissionText')}</p>
        </div>
      </div>
    </div>

    {/* 03 - Core Objectives */}
    <div className="relative flex items-start">
      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg">
        <span className="text-white font-bold text-lg">03</span>
      </div>
      <div className="ml-8 flex-1">
        <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-teal-500">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('coreObjectivesTitle')}</h3>
          <p className="text-gray-700 leading-relaxed text-lg">{t('coreObjectivesText')}</p>
        </div>
      </div>
    </div>


    {/* 04 - Structure & Membership */}
    <div className="relative flex items-start">
      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full flex items-center justify-center shadow-lg">
        <span className="text-white font-bold text-lg">04</span>
      </div>
      <div className="ml-8 flex-1">
        <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-cyan-500">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('structureMembershipTitle')}</h3>
          <p className="text-gray-700 leading-relaxed text-lg">{t('structureMembershipText')}</p>
        </div>
      </div>
    </div>

    {/* 05 - Key Program Areas */}
    <div className="relative flex items-start">
      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg">
        <span className="text-white font-bold text-lg">05</span>
      </div>
      <div className="ml-8 flex-1">
        <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-teal-500">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('keyProgramAreasTitle')}</h3>
          <p className="text-gray-700 leading-relaxed text-lg">{t('keyProgramAreasText')}</p>
        </div>
      </div>
    </div>

    {/* 06 - Partnerships & Target Groups */}
    <div className="relative flex items-start">
      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full flex items-center justify-center shadow-lg">
        <span className="text-white font-bold text-lg">06</span>
      </div>
      <div className="ml-8 flex-1">
        <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-cyan-500">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('partnershipsTitle')}</h3>
          <p className="text-gray-700 leading-relaxed text-lg">{t('partnershipsText')}</p>
        </div>
      </div>
    </div>


    {/* 07 - Guiding Principles & Future Direction */}
    <div className="relative flex items-start">
      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg">
        <span className="text-white font-bold text-lg">07</span>
      </div>
      <div className="ml-8 flex-1">
        <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-teal-500">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('guidingPrinciplesTitle')}</h3>
          <p className="text-gray-700 leading-relaxed text-lg">{t('guidingPrinciplesText')}</p>
        </div>
      </div>
    </div>
  </div>

  {/* Tagline Section */}
  <div className="mt-20 text-center">
    <div className="bg-gradient-to-r from-teal-500 via-cyan-500 to-teal-500 p-12 rounded-2xl shadow-2xl">
      <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">{t('aboutTaglineTitle')}</h3>
      <p className="text-xl text-teal-100">{t('aboutTaglineSub')}</p>
    </div>
  </div>
</div>

          </div>
        </section>

        {/* Our Partners Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">{t('partnersHeading')}</h2>
              <div className="w-24 h-1 bg-gradient-to-r from-teal-500 to-cyan-500 mx-auto mb-6"></div>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">{t('partnersIntro')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 items-center">
              {/* ZOA */}
              <div className="text-center group">
                <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  <img
                    src="/zoalogo.png"
                    alt="ZOA Logo"
                    className="w-32 h-32 mx-auto object-contain mb-4 group-hover:brightness-110 transition-all duration-300"
                  />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{t('partnerZOATitle')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{t('partnerZOADesc')}</p>
                </div>
              </div>

              {/* We Are Able */}
              <div className="text-center group">
                <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  <img
                    src="/we-are-able.png"
                    alt="We Are Able Logo"
                    className="w-32 h-32 mx-auto object-contain mb-4 group-hover:brightness-110 transition-all duration-300"
                  />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{t('partnerWAATitle')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{t('partnerWAADesc')}</p>
                </div>
              </div>

              {/* DPO networks */}
              <div className="text-center group">
                <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  <img
                    src="/dpologo.png"
                    alt="DPO networks Logo"
                    className="w-32 h-32 mx-auto object-contain mb-4 group-hover:brightness-110 transition-all duration-300"
                  />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{t('partnerDPOTitle')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{t('partnerDPODesc')}</p>
                </div>
              </div>
              {/*  international partners */}
              <div className="text-center group">
                <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  <img
                    src="/internationalpartners.png"
                    alt="international partners logo"
                    className="w-32 h-32 mx-auto object-contain mb-4 group-hover:brightness-110 transition-all duration-300"
                  />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{t('partnerInternationalTitle')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{t('partnerInternationalDesc')}</p>
                </div>
              </div>
              {/* Civil Society Organizations */}
              <div className="text-center group">
                <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  <img
                    src="/civil-society-organization-logo.png"
                    alt="civil society organizations logo"
                    className="w-32 h-32 mx-auto object-contain mb-4 group-hover:brightness-110 transition-all duration-300"
                  />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{t('partnerCSOTitle')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{t('partnerCSODesc')}</p>
                </div>
              </div>
              {/* Government offices (Labour & Social Affairs, Women & Social Protection, Education, Health, etc.) */}
              <div className="text-center group">
                <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  <img
                    src="/governmentlogo.png"
                    alt="government offices logo"
                    className="w-32 h-32 mx-auto object-contain mb-4 group-hover:brightness-110 transition-all duration-300"
                  />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{t('partnerGovTitle')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{t('partnerGovDesc')}</p>
                </div>
              </div>
            </div>

            {/* Call to Partnership */}
            <div className="mt-16 text-center">
              <div className="bg-gradient-to-r from-teal-500 via-cyan-500 to-teal-500 p-8 rounded-2xl shadow-2xl">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('callPartnershipTitle')}</h3>
                <p className="text-lg text-teal-100 mb-6 max-w-2xl mx-auto">{t('callPartnershipDesc')}</p>
                <a
                  href="/contactus"
                  className="inline-block px-8 py-3 rounded-lg text-lg font-semibold bg-white text-teal-600 hover:bg-gray-100 transition-colors duration-300 shadow-md"
                >
                  {t('callPartnershipCta')}
                </a>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default AboutUsPage;